import React, {
  FunctionComponent,
  memo,
  PropsWithChildren,
  ReactElement,
  useCallback,
  useContext,
  useState,
} from 'react'

import Tab, { TabProps } from '@components/Tab/Tab'
import Markdown from '@components/Markdown/Markdown'
import VerticalChoices from '@components/AnswerInput/OneOfManyInput/VerticalChoices/VerticalChoices'
import { OneOfManyQuestion } from '@models/Survey'
import { AnswerType } from '@models/Survey/Answer'
import './TabbedPane.scss'
import robot from '@assets/images/characters/robot-head-talking.gif'
import { PredictionResults } from '@models/Prediction/Prediction'
import { useTranslation } from 'react-i18next'
import { SurveyContext } from '@context/SurveyContext'
import { useNavigate } from 'react-router-dom'
import capitalize from 'lodash/capitalize'
import ModalDialog, { handleImageMouseEnterOrLeave, handleInfoUrl } from '@components/ModalDialog/ModalDialog'
import InfoIdleIcon from '@assets/images/icons/Info-idle.png'
import InfoHoverIcon from '@assets/images/icons/Info-hover.png'
import { SurveyMode } from '@reducers/SurveyReducer'
import { PredictionBars } from './PredictionBars'
import EditionProgressBar from './EditionProgressBar'
import useIntersectionObserver from '@hooks/useIntersectionObserver'

export interface OwnProps {
  tabs: TabProps[]
  headerText?: string
  contentText?: string | JSX.Element
  choices?: OneOfManyQuestion
  onChoiceSelection?: (answer: AnswerType) => void
  propClassName?: string
  withRobot?: boolean
  predictionResults?: PredictionResults
}

type Props = PropsWithChildren<OwnProps>

export const TabbedPane: FunctionComponent<Props> = ({
  tabs,
  headerText,
  contentText,
  choices,
  onChoiceSelection,
  propClassName = '',
  withRobot = false,
  children,
  predictionResults,
}: Props): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation('ModalDialog')
  const className = `TabbedPane ${propClassName}`
  const { currentQuestion, mode } = useContext(SurveyContext)
  const [currentAnswer, setCurrentAnswer] = useState<undefined | AnswerType>(undefined)
  const [infoDialogVisible, setInfoDialogVisible] = useState(false)
  const navigate = useNavigate()
  const isSandbox = [SurveyMode.SANDBOX, SurveyMode.SANDBOX_PORTRAIT].includes(mode)
  const [isSticky, setIsSticky] = useState(false);

  const handleSelection = useCallback(
    (answer: AnswerType) => {
      setCurrentAnswer(answer)
      if (typeof answer === 'object' && Object.hasOwn(answer, 'value')) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return onChoiceSelection?.((answer as any).value)
      }

      return onChoiceSelection?.(answer)
    },
    [onChoiceSelection],
  )

  const closeInfoModal = useCallback(() => {
    setInfoDialogVisible(false)
  }, [setInfoDialogVisible])

  const question = currentQuestion as OneOfManyQuestion
  const meta = question?.meta
  const infoText = meta?.[`info${capitalize(language)}` as keyof typeof meta] as string | undefined

  const handleInfoModalAction = useCallback(
    (url: string) => {
      closeInfoModal()
      handleInfoUrl(url, navigate)
    },
    [closeInfoModal, navigate],
  )

  const showInfoModal = useCallback(() => {
    if (infoText) {
      setInfoDialogVisible(true)
    }
  }, [infoText, setInfoDialogVisible])

  const stickyRef = useIntersectionObserver<HTMLDivElement>(
    (isSticky) => setIsSticky(isSticky),
    {
      threshold: [1],
      rootMargin: '-8px 0px 0px 0px'
    }
  );

  return (
    <div className={className}>
      {!isSandbox && infoText && (
        <ModalDialog
          bodyComponent={<Markdown content={infoText} />}
          onClose={closeInfoModal}
          visible={infoDialogVisible}
          actionText={t('action.more')}
          action={() => {
            if (meta?.infoUrl) {
              handleInfoModalAction(meta.infoUrl)
            }
          }}
        />
      )}
      <div className="TabbedPane-body">
        <div ref={stickyRef} className="sticky-elem-anchor"/>
        <div className={`TabbedPane-body-navigation ${isSticky ? 'is-sticky' : ''}`}>
          <div className="TabbedPane-body-navigation-tabs">
            {tabs.map(({ text, active, onClick }) => (
              <Tab key={text} text={text} active={active} onClick={onClick} />
            ))}
          </div>
          {!isSandbox && infoText && (
            <div className="TabbedPane-body-navigation-infoIcon">
              <img
                src={InfoIdleIcon}
                onClick={showInfoModal}
                onMouseOver={(e) => handleImageMouseEnterOrLeave(e, InfoHoverIcon)}
                onMouseLeave={(e) => handleImageMouseEnterOrLeave(e, InfoIdleIcon)}
              />
            </div>
          )}
          {headerText && !predictionResults && <h2 className="TabbedPane-body-navigation-text">{headerText}</h2>}
          <PredictionBars predictionResults={predictionResults} />
          <EditionProgressBar />
        </div>

        <div className="TabbedPane-body-question">
          {contentText && (
            <div key="md" className="TabbedPane-body-question-text">
              {typeof contentText === 'string' ? <Markdown content={contentText} /> : <>{contentText}</>}
            </div>
          )}
          {choices && (
            <VerticalChoices
              key="vc"
              className={`TabbedPane-body-question-choices item-cnt-${choices.choices.length}`}
              question={choices}
              answer={currentAnswer}
              onSelection={handleSelection}
            />
          )}
          {children}
          {withRobot && <img className="robot" src={robot} />}
        </div>
      </div>
    </div>
  )
}

export default memo(TabbedPane)
